import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCalendar, faClock, faCheck, faLanguage } from '@fortawesome/free-solid-svg-icons';
import { Attendee, Request } from '../types';
import Modal from '../components/common/Modal';
import RequestActions from '../components/request/RequestActions';
import getFileURL from '../utils/getFileURL';
import './RequestView.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { attendeeSet } from '../store/attendeeSlice';

export interface RequestViewProps {
    request?: Request;
    isOpen: boolean;
    onClose: () => void;
};

const RequestView: FC<RequestViewProps> = ({ request, isOpen, onClose }) => {
	const { t, i18n } = useTranslation();
	const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const company = i18n.language === 'en' ? 'company_en' : 'company_ru';
    const date_loc = i18n.language === 'en' ? 'date_en' : 'date_ru';
    const duration_loc = i18n.language === 'en' ? ' min' : ' мин';
    const user = useAppSelector(store => store.auth.user);
    const slots = useAppSelector(store => store.slots);
    const attendees = useAppSelector(store => store.attendees);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [ date, setDate ] = useState('unknown');
    const [ time, setTime ] = useState('unknown');
    const [ duration, setDuration ] = useState('unknown');
    const [ error, setError ] = useState('');
    const [ respondent, setRespondent ] = useState<Attendee|undefined>(undefined);
    const [ needTranslator, setNeedTranslator ] = useState(false);

    useEffect(() => {
        if(!request || !user) return;
        setError('');
        setDate('unknown');
        setTime('unknown');
        setDuration('unknown');
        setRespondent(undefined);
        setNeedTranslator(false);

        const slot = slots.find(s => s.id === request.slot_id);
        if(!slot) {
            setError(t('request.noslot') as string);
            return;
        }

        let respondent: Attendee|undefined;
        if(request.from_id === user.id) {
            respondent = attendees.find(a => a.id === request.to_id);
        } else {
            respondent = attendees.find(a => a.id === request.from_id);
        }
        setRespondent(respondent);

        if(respondent) {
            const intersection = user.languages.filter(value => respondent?.languages.includes(value));
            if(intersection.length < 1) setNeedTranslator(true);
        }

        const [ hour, min ] = slot.start.split('T')[1].split(':');
        setDate(slot[date_loc]);
        setTime(`${hour}:${min}`);
        setDuration(slot.duration + duration_loc);

    }, [ attendees, user, request, slots, t, date_loc, duration_loc ]);

    const gotoRespondent = () => {
        if(respondent) {
            onClose();
            // Попап может быть вызван из своего календаря и из календаря собеседника.
            // Поэтому нам необходимо понять где мы сейчас находимся чтобы сделать переход
            // на страницу собеседника или же просто закрыть попап.
            if(!location.pathname.endsWith('attendee')) {
                dispatch(attendeeSet(respondent));
                navigate('attendee');
            }
        }
    };

    if(!request || !user) return null;

	return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <h1>{request.status === 'approved' ? t('request.title.meeting') : t('request.title.request')}</h1>
            {error && <p className='error'>{error}</p>}
            <div className='RequestView'>
                <img alt='avatar' className='Avatar' src={getFileURL(respondent?.avatar)}/>
                
                {respondent && <div className='Info'>
                    <div className='Name' onClick={() => gotoRespondent()}>{respondent[name]}</div>
                    <div>{respondent[company]}</div>
                </div>}

                <div className='Info'>
                    <ul className='fa-ul'>
                    <li>
                        <span className='fa-li'><Icon fixedWidth icon={faCalendar}/></span>
                        {date} {time} - {duration}
                    </li>

                    {needTranslator &&
                    <li>
                        <span className='fa-li'><Icon fixedWidth icon={faLanguage}/></span>
                        {t('request.need_trans')}
                    </li>}

                    {request.status === 'approved' && 
                    <li className='success'>
                        <span className="fa-li"><Icon fixedWidth icon={faCheck}/></span>
                        {t('request.approved')}
                    </li>}

                    {request.status === 'negotiation' &&
                    <li>
                        <span className="fa-li"><Icon fixedWidth icon={faClock}/></span>
                        {request.actor_id === user.id ? t('request.waiting') : t('request.needaction')}
                    </li>}
                    </ul>
                </div>
            </div>
            <RequestActions request={request}/>
        </Modal>
	);
};

export default RequestView;
